import { useState, FormEvent } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import SchemaOrg, { makeWebPageSchema, makeBreadcrumbSchema, makeOrganizationSchema } from '@/components/feature/SchemaOrg';

const SUBMIT_ADDR = 'https://readdy.ai/api/form/d9otroiftmtnfcfh0e50';

const teamValues = [
  {
    icon: 'ri-heart-pulse-line',
    title: '热爱韩漫',
    desc: '团队每一位成员都是韩漫的忠实爱好者，我们用热爱驱动每一篇推荐和每一份内容。',
  },
  {
    icon: 'ri-compass-3-line',
    title: '用心导航',
    desc: '我们精心筛选每一部漫画和每一个平台入口，只为帮你节省寻找好内容的时间。',
  },
  {
    icon: 'ri-shield-check-line',
    title: '真实推荐',
    desc: '所有推荐和评价均基于真实阅读体验，绝不接受未标注的商业推广内容。',
  },
  {
    icon: 'ri-group-line',
    title: '社区共建',
    desc: '我们相信好的内容社区是由读者和编辑共同打造的，欢迎每一位漫迷的参与和反馈。',
  },
];

const testimonials = [
  {
    name: '漫画迷小王',
    role: '资深韩漫读者',
    text: '自从用了韩漫大全，找漫画的时间大大减少了。分类清晰，推荐靠谱，已经成为我每天必刷的网站！',
    rating: 5,
  },
  {
    name: '二次元少女',
    role: '漫画博主',
    text: '作为一个经常需要推荐漫画的博主，韩漫大全是我最重要的工具站之一。资源丰富，更新及时，强烈推荐。',
    rating: 5,
  },
  {
    name: '办公室小李',
    role: '新晋韩漫迷',
    text: '刚入门韩漫的时候不知道从哪开始，朋友推荐了这个网站。按分类一个个看下来，现在已经停不下来了哈哈。',
    rating: 5,
  },
  {
    name: '夜读侠',
    role: '夜班族漫迷',
    text: '上夜班的时候全靠韩漫解闷。这个网站的分类做得很细致，悬疑惊悚类推荐特别对胃口，每部都好看！',
    rating: 5,
  },
  {
    name: '学生党小张',
    role: '大学生',
    text: '课余时间喜欢看漫画放松，韩漫大全帮我发现了很多冷门好作品。校园青春类的推荐特别赞！',
    rating: 5,
  },
  {
    name: '文艺青年阿琳',
    role: '内容创作者',
    text: '剧情深度类的韩漫推荐让我找到了很多很有文学价值的作品。网站的文章质量也很高，分析很到位。',
    rating: 5,
  },
];

export default function About() {
  const [formData, setFormData] = useState({ name: '', email: '', message: '' });
  const [formStatus, setFormStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [formMessage, setFormMessage] = useState('');

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const honeypotInput = form.querySelector<HTMLInputElement>('input[name="website_alt"]');
    if (honeypotInput && honeypotInput.value.trim() !== '') {
      setFormStatus('success');
      setFormMessage('消息已发送！');
      return;
    }

    setFormStatus('loading');
    try {
      const formDataObj = new FormData(form);
      formDataObj.delete('website_alt');
      const res = await fetch(SUBMIT_ADDR, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formDataObj as unknown as Record<string, string>).toString(),
      });
      const responseText = await res.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }
      if (res.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        setFormMessage('感谢您的留言！我们会尽快回复。');
        setFormData({ name: '', email: '', message: '' });
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || responseText || '发送失败';
        setFormStatus('error');
        setFormMessage(serverMsg.includes('spam') ? '发送失败，请稍后重试。' : serverMsg);
      }
    } catch {
      setFormStatus('error');
      setFormMessage('网络错误，请稍后重试。');
    }
  };

  return (
    <>
      <SchemaOrg {...makeOrganizationSchema()} />
      <SchemaOrg {...makeWebPageSchema(
        '关于我们 - 韩漫大全',
        '韩漫大全品牌介绍、用户口碑与联系方式。了解我们的团队故事和服务理念。',
        'https://www.example.com/about',
      )} />
      <SchemaOrg {...makeBreadcrumbSchema([
        { name: '首页', url: 'https://www.example.com/' },
        { name: '关于我们', url: 'https://www.example.com/about' },
      ])} />
      <Navbar />
      <main className="pt-16">
        {/* Breadcrumb */}
        <nav className="px-4 md:px-6 py-4 bg-background-100" aria-label="面包屑导航">
          <div className="max-w-5xl mx-auto flex items-center gap-2 text-xs text-foreground-500">
            <Link to="/" className="hover:text-primary-600 transition-colors">首页</Link>
            <i className="ri-arrow-right-s-line text-foreground-400"></i>
            <span className="text-foreground-700 font-medium">关于我们</span>
          </div>
        </nav>

        {/* Brand Intro */}
        <section className="py-14 md:py-18 px-4 md:px-6 bg-background-50">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-12">
              <span className="inline-block px-3 py-1 bg-primary-100 text-primary-700 rounded-full text-xs font-medium mb-4">
                关于我们
              </span>
              <h1 className="font-heading text-3xl md:text-4xl font-bold text-foreground-950 mb-4">
                韩漫大全 — 你的韩漫资源导航站
              </h1>
              <p className="text-foreground-600 text-sm max-w-2xl mx-auto leading-relaxed">
                我们是一个专注于韩国漫画资源整合与推荐的平台。无论是刚入坑的新手还是资深的韩漫爱好者，
                都能在这里找到优质的韩漫资源和阅读入口。通过精心的分类整理和真实的用户评价，
                我们致力于成为中文互联网上最值得信赖的韩漫导航站点。
              </p>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 md:gap-6 mb-14">
              {[
                { value: '5000+', label: '收录漫画' },
                { value: '50万+', label: '月活跃用户' },
                { value: '8个', label: '精选分类' },
                { value: '2020年', label: '成立时间' },
              ].map((stat) => (
                <div key={stat.label} className="text-center p-5 bg-background-100 rounded-xl">
                  <div className="text-2xl md:text-3xl font-bold text-primary-600 font-heading">
                    {stat.value}
                  </div>
                  <div className="text-foreground-500 text-xs mt-1">{stat.label}</div>
                </div>
              ))}
            </div>

            {/* Values */}
            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 text-center mb-8">
              我们的价值观
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
              {teamValues.map((val) => (
                <div key={val.title} className="p-5 md:p-6 bg-background-100 rounded-xl text-center">
                  <div className="w-12 h-12 flex items-center justify-center bg-primary-100 rounded-xl mx-auto mb-4">
                    <i className={`${val.icon} text-xl text-primary-600`}></i>
                  </div>
                  <h4 className="font-heading font-semibold text-foreground-900 text-sm md:text-base mb-2">
                    {val.title}
                  </h4>
                  <p className="text-foreground-500 text-xs leading-relaxed">{val.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Reputation / Testimonials */}
        <section className="py-14 md:py-18 px-4 md:px-6 bg-background-100">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-10">
              <span className="inline-block px-3 py-1 bg-accent-100 text-accent-700 rounded-full text-xs font-medium mb-3">
                用户口碑
              </span>
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-2">
                听听漫迷们怎么说
              </h2>
              <p className="text-foreground-600 text-sm">
                来自真实用户的评价和反馈
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
              {testimonials.map((t) => (
                <div key={t.name} className="p-5 md:p-6 bg-background-50 rounded-xl">
                  <div className="flex items-center gap-1 mb-3">
                    {Array.from({ length: t.rating }).map((_, i) => (
                      <i key={i} className="ri-star-fill text-accent-400 text-sm"></i>
                    ))}
                  </div>
                  <p className="text-foreground-600 text-sm leading-relaxed mb-4">
                    &ldquo;{t.text}&rdquo;
                  </p>
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-full bg-primary-100 flex items-center justify-center">
                      <span className="text-primary-600 font-bold text-xs">
                        {t.name.charAt(0)}
                      </span>
                    </div>
                    <div>
                      <div className="text-foreground-900 text-sm font-medium">{t.name}</div>
                      <div className="text-foreground-400 text-xs">{t.role}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Contact Form */}
        <section className="py-14 md:py-18 px-4 md:px-6 bg-background-50">
          <div className="max-w-2xl mx-auto">
            <div className="text-center mb-10">
              <span className="inline-block px-3 py-1 bg-primary-100 text-primary-700 rounded-full text-xs font-medium mb-3">
                联系我们
              </span>
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-2">
                有任何问题或建议？
              </h2>
              <p className="text-foreground-600 text-sm">
                欢迎通过以下方式联系我们，我们会在24小时内回复
              </p>
            </div>

            {/* Contact Info Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-10">
              <div className="p-5 bg-background-100 rounded-xl text-center">
                <div className="w-10 h-10 flex items-center justify-center bg-primary-100 rounded-lg mx-auto mb-3">
                  <i className="ri-mail-line text-primary-600"></i>
                </div>
                <h4 className="font-heading text-sm font-semibold text-foreground-900 mb-1">电子邮箱</h4>
                <p className="text-foreground-500 text-xs">contact@manhwa123.com</p>
              </div>
              <div className="p-5 bg-background-100 rounded-xl text-center">
                <div className="w-10 h-10 flex items-center justify-center bg-primary-100 rounded-lg mx-auto mb-3">
                  <i className="ri-wechat-line text-primary-600"></i>
                </div>
                <h4 className="font-heading text-sm font-semibold text-foreground-900 mb-1">微信公众号</h4>
                <p className="text-foreground-500 text-xs">韩漫大全</p>
              </div>
              <div className="p-5 bg-background-100 rounded-xl text-center">
                <div className="w-10 h-10 flex items-center justify-center bg-primary-100 rounded-lg mx-auto mb-3">
                  <i className="ri-timer-line text-primary-600"></i>
                </div>
                <h4 className="font-heading text-sm font-semibold text-foreground-900 mb-1">响应时间</h4>
                <p className="text-foreground-500 text-xs">24小时内回复</p>
              </div>
            </div>

            <form
              onSubmit={handleSubmit}
              data-readdy-form="true"
              className="space-y-4"
            >
              <input
                type="text"
                name="website_alt"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                readOnly
                className="form-website-alt-field"
              />
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-foreground-700 text-xs font-medium mb-1.5">姓名</label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full px-4 py-2.5 rounded-lg bg-background-100 text-foreground-900 text-sm outline-none border border-background-200 focus:border-primary-300 transition-colors"
                    required
                  />
                </div>
                <div>
                  <label className="block text-foreground-700 text-xs font-medium mb-1.5">邮箱</label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="w-full px-4 py-2.5 rounded-lg bg-background-100 text-foreground-900 text-sm outline-none border border-background-200 focus:border-primary-300 transition-colors"
                    required
                  />
                </div>
              </div>
              <div>
                <label className="block text-foreground-700 text-xs font-medium mb-1.5">留言内容</label>
                <textarea
                  name="message"
                  value={formData.message}
                  onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                  rows={5}
                  maxLength={500}
                  className="w-full px-4 py-2.5 rounded-lg bg-background-100 text-foreground-900 text-sm outline-none border border-background-200 focus:border-primary-300 transition-colors resize-none"
                  required
                />
                <p className="text-foreground-400 text-xs mt-1">{formData.message.length}/500</p>
              </div>
              <button
                type="submit"
                disabled={formStatus === 'loading'}
                className="w-full py-3 bg-primary-500 hover:bg-primary-600 text-white font-medium rounded-lg transition-all duration-200 text-sm whitespace-nowrap cursor-pointer disabled:opacity-60"
              >
                {formStatus === 'loading' ? (
                  <span className="flex items-center justify-center gap-2">
                    <i className="ri-loader-4-line animate-spin"></i>
                    发送中...
                  </span>
                ) : (
                  <>
                    <i className="ri-send-plane-line mr-1.5"></i>
                    发送留言
                  </>
                )}
              </button>
              {formStatus === 'success' && (
                <p className="text-primary-600 text-xs text-center flex items-center justify-center gap-1">
                  <i className="ri-check-line"></i>
                  {formMessage}
                </p>
              )}
              {formStatus === 'error' && (
                <p className="text-red-500 text-xs text-center flex items-center justify-center gap-1">
                  <i className="ri-error-warning-line"></i>
                  {formMessage}
                </p>
              )}
            </form>
          </div>
        </section>
      </main>
      <Footer />
      <ScrollToTop />
    </>
  );
}